import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React, { FC, useEffect } from 'react';

import NotFound from 'pages/404';
import { ParsedUrlQuery } from 'querystring';

import { ManufacturerProfile, PermissionCompany, PublishedStatus, QrScanType } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { CryptoHelpers } from 'helpers/CryptoHelpers';
import { getParam } from 'helpers/get-param';
import { ImageKitHelpers } from 'helpers/ImageKitHelpers';
import { redirectAnonymous } from 'helpers/redirect-anonymous';
import { URLHelpers } from 'helpers/URLHelpers';

import { CompanyProfile } from 'components/company-profile/CompanyProfile';
import { QrScanGate } from 'components/showtime/components/QrScanGate';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentUser } from 'hooks/use-current-user';
import { CompanyProfileService } from 'services/CompanyProfileService';
import { RouterService } from 'services/RouterService';
import { UserService } from 'services/UserService';

const ManufacturerPage: FC<{ company: ManufacturerProfile }> = ({ company }) => {
    const { query } = useRouter();

    const user = useCurrentUser();
    const team = useCurrentTeam();

    // TODO: remove when we fully move from Manufacturers/Distributors to Profile
    useEffect(() => {
        // No problem if manufacturer is already set
        if (company) {
            return;
        }

        const currentPath = window.location.pathname;

        // Current path is not a redirect to /profile
        if (!currentPath.includes('/manufacturers')) {
            return;
        }

        // router.query not available when using a redirect from a URL that doesn't match a route
        const slug = window.location.pathname.split('/').reverse()?.[0];

        if (slug) {
            const url = CompanyHelpers.urls.view(slug);
            RouterService.replace(url).then();
        }
    }, [company]);

    useEffect(() => {
        UserService.pingProfile(company.id);
    }, [company.id]);

    const userIsDeveloper = user?.developer;
    const userIsCompanyTeamMember = company?.team === team?.id;
    const companyIsPublished = company?.status === PublishedStatus.PUBLISHED;
    const isPreviewUrl = query.preview === CryptoHelpers.unsafeSimpleHash(company?.slug);

    return userIsDeveloper || userIsCompanyTeamMember || companyIsPublished || isPreviewUrl ? (
        <>
            <CompanyProfile company={company} />
            <QrScanGate company={company} type={QrScanType.PROFILE} itemId={company.id} title={company.name} />
        </>
    ) : (
        <NotFound />
    );
};

export const getServerSideProps: GetServerSideProps = async ({ resolvedUrl, params, req }) => {
    const { company, notFoundOrRedirect } = await getCompany(resolvedUrl, params, req, { allowAnonymous: true });

    let image = '';
    let description = '';

    try {
        // @ts-ignore
        image = ImageKitHelpers.getSrcFromFile(company?.logos?.large || company?.logos?.small);
        image = image ? URLHelpers.og(image) : '';
    } catch {
        // Progressive enhancement!
    }

    if (company) {
        description = `Discover ${company.name} products, services and more on DEP`;
    }

    return (
        notFoundOrRedirect || {
            props: {
                company,
                metadata: {
                    title: company.name,
                    description,
                    image,
                },
            },
        }
    );
};

export const getCompany = async (
    resolvedUrl: string,
    params: ParsedUrlQuery | undefined,
    req: GetServerSidePropsContext['req'],
    { allowAnonymous = false }: { allowAnonymous?: boolean } = {},
) => {
    if (!allowAnonymous) {
        const redirect = await redirectAnonymous({ req, resolvedUrl });

        if (redirect) {
            return {
                notFoundOrRedirect: redirect,
            } as const;
        }
    }

    const slugOrId = getParam(params, 'slug');

    if (!slugOrId) {
        return {
            notFoundOrRedirect: {
                notFound: true,
            },
        } as const;
    }

    const company = await CompanyProfileService.getBySlug(slugOrId, { req });

    if (!company) {
        return {
            notFoundOrRedirect: {
                notFound: true,
            },
        } as const;
    }

    if (company.id === slugOrId && company.slug) {
        return {
            notFoundOrRedirect: {
                redirect: {
                    destination: resolvedUrl.replace(slugOrId, company.slug),
                    permanent: true,
                },
            },
        };
    }

    return {
        slugOrId,
        company,
    };
};

export const getEditableCompany = async (
    resolvedUrl: string,
    params: ParsedUrlQuery | undefined,
    req: GetServerSidePropsContext['req'],
) => {
    const { company, notFoundOrRedirect } = await getCompany(resolvedUrl, params, req);

    if (notFoundOrRedirect) {
        return { notFoundOrRedirect };
    }

    const { permissions } = company as unknown as ManufacturerProfile;

    if (!permissions?.includes(PermissionCompany.EDIT)) {
        return {
            notFoundOrRedirect: {
                notFound: true,
            },
        } as const;
    }

    return { company };
};

export default ManufacturerPage;
